import { useState } from "react";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import { Button, InlineAlert, LoadingSpinner, SearchableSelect } from "../BaseComponents"
import Label from "../BaseComponents/Label";
import CanonicalModal from "../BaseComponents/CanonicalModal/CanonicalModal"

type AddToPortfolioModalProps = {
    open?: boolean,
    onClose: () => void,
    companyId: string,
    companyName: string,
    existingAttachments?: any[],
}

function AddToPortfolioModal({open=false, onClose, companyId, companyName, existingAttachments=[]}: AddToPortfolioModalProps) {
    const [loading, setLoading] = useState(false);
    // Issue #884: both loadPortfolios (GET) and onSave (POST) previously had no catch at all -
    // under an active admin/delegated assumption, either can now reject with the notify-only
    // READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE codes (#857 item 6) instead of hanging forever,
    // which surfaced as an unhandled rejection with the spinner stuck on and no visible feedback.
    const [error, setError] = useState('');

    const [portfolioId, setPortfolioId] = useState('');
    const [portfolios, setPortfolios] = useState<any[]>([]);

    const api = useApi();

    const availablePortfolios = portfolios.filter(portfolio => {
        return !existingAttachments.some(attached => {
            return attached.portfolio.id === portfolio.id
        })
    })

    const loadPortfolios = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await api.get('/portfolios/');
            setPortfolios(result.data.portfolios);
        } catch (err) {
            setPortfolios([]);
            setError(getApiErrorMessage(err, 'Failed to load portfolios. Please try again.'));
        } finally {
            setLoading(false);
        }
    }

    const onSave = async () => {
        setLoading(true);
        setError('');
        try {
            await api.post(`/portfolios/${portfolioId}/companies/`, {
                _csrf: await getCSRFToken(),
                companyId: companyId,
            })
            // Only reached on success - a denied/unconfirmed write must not close the modal as
            // if it had been added.
            onClose();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Failed to add this organisation to the portfolio. Please try again.'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <CanonicalModal
            open={open}
            onClose={onClose}
            onOpen={loadPortfolios}
            size="small"
            title="Add to portfolio"
            actions={
                loading ? null : (
                    <>
                        <Button
                            disabled={!portfolioId}
                            type="Primary"
                            text="Add organisation to portfolio"
                            onClick={onSave}
                            additionalClass="mr-5"
                        />
                        <Button type="Outlined" text="Cancel" onClick={onClose} />
                    </>
                )
            }
        >
            { (loading) ? (
                <LoadingSpinner />
            ) : (
                <>
                    <p className="text-white mt-8">Add { companyName} to a portfolio</p>

                    { existingAttachments.length ? (
                        <div className="mt-4">
                            <Label>Currently in portfolios:</Label>
                            { existingAttachments.map(attached => {
                                return (
                                    <div className="text-white">
                                        { attached.portfolio.name }
                                    </div>
                                )
                            })}
                        </div>
                    ): (
                        null
                    )}

                    <Label className="block mt-4">Select a portfolio</Label>
                    <SearchableSelect
                        fillContainer
                        placeholder="Select a portfolio"
                        options={availablePortfolios.map(portfolio => ({ value: portfolio.id, label: portfolio.name }))}
                        value={availablePortfolios
                            .map(portfolio => ({ value: portfolio.id, label: portfolio.name }))
                            .find(option => option.value === portfolioId)}
                        onChange={(option) => setPortfolioId(option?.value ?? '')}
                    />

                    { error && (
                        <InlineAlert type='error' className='mt-4'>{ error }</InlineAlert>
                    )}
                </>
            )}
        </CanonicalModal>
    )
}

export default AddToPortfolioModal
